import React, { useCallback, useEffect, useState } from "react";
import {
  X, Loader2, CheckCircle2, Undo2, Trash2, Mail, Pencil, ShieldAlert, RefreshCw, Download,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { superAdminApi, adminApi, centsToAUD, formatAuDate } from "../../lib/events";
import { extractError } from "../../lib/api";

const STATUS_TONE = {
  valid: "bg-emerald-100 text-emerald-800",
  checked_in: "bg-sky-100 text-sky-800",
  voided: "bg-red-100 text-red-800",
  cancelled: "bg-red-100 text-red-800",
  refunded: "bg-amber-100 text-amber-800",
};

function StatusPill({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${STATUS_TONE[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

export default function BookingDrawer({ orderId, onClose, onDeleteTicket, onDeleteBooking, onMutated, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      setData(await superAdminApi.booking(orderId));
    } catch (e) {
      toast.error(extractError(e));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [orderId, onClose]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doAction = async (ticketId, fn, okMsg) => {
    setBusyId(ticketId);
    try { await fn(); toast.success(okMsg); onMutated && onMutated(); await load(); }
    catch (e) { toast.error(extractError(e)); }
    finally { setBusyId(null); }
  };

  const doDownload = async (t) => {
    setBusyId(t.id);
    try {
      const historical = ["voided", "cancelled", "refunded"].includes(t.status);
      const { blob, headers } = await adminApi.downloadTicketPdf(t.id, historical);
      const cd = (headers && (headers["content-disposition"] || headers["Content-Disposition"])) || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `getickets-ticket-${t.code}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Ticket downloaded");
    } catch (e) {
      toast.error("Could not download ticket PDF");
    } finally {
      setBusyId(null);
    }
  };

  const doPurge = async (ticketId) => {
    if (!window.confirm("Permanently purge this internal/test ticket? This cannot be undone.")) return;
    setBusyId(ticketId);
    try {
      const res = await superAdminApi.purgeTicket(ticketId, { reason: "purge internal test" });
      toast.success("Purged");
      onMutated && onMutated();
      if (res.order_purged) onClose(); else await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusyId(null); }
  };

  const order = data?.order;
  const event = data?.event;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="sa-booking-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto border-l-2 border-[var(--gt-ink)] shadow-[-8px_0_0_0_rgba(0,0,0,0.06)]">
        <div className="sticky top-0 bg-[var(--gt-ink)] text-white px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--gt-ink-3)]">Booking</div>
            <div className="font-heading text-lg font-black tracking-tight" data-testid="sa-drawer-title">
              {order ? (order.id || "").slice(0, 8) : "…"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="text-white/80 hover:text-white p-1" onClick={load} title="Refresh"><RefreshCw size={16} /></button>
            <button className="text-white/80 hover:text-white p-1" onClick={onClose} data-testid="sa-drawer-close"><X size={20} /></button>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin" /></div>
        ) : !data ? null : (
          <div className="p-5 space-y-5">
            {/* Order summary */}
            <div className="border-2 border-[var(--gt-ink)] p-4 text-sm space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="font-heading text-base font-black">{event?.title}</div>
                <StatusPill status={order?.status} />
              </div>
              <Row k="Buyer" v={`${order?.buyer_name || "—"} · ${order?.buyer_email || ""}`} />
              <Row k="Source" v={order?.order_source} />
              {order?.record_mode && <Row k="Record mode" v={order.record_mode} />}
              {order?.internal_test && <div className="text-[var(--gt-accent)] font-bold text-xs">INTERNAL / TEST</div>}
              <Row k="Paid" v={order?.amount_total_cents ? centsToAUD(order.amount_total_cents) : "Free"} />
              {order?.refunded_cents > 0 && <Row k="Refunded" v={centsToAUD(order.refunded_cents)} />}
              {order?.cancellation_status && <Row k="Cancellation" v={order.cancellation_status} />}
              {order?.stripe_payment_intent_id && <Row k="Stripe PI" v={order.stripe_payment_intent_id} mono />}
            </div>

            <div className="flex items-center justify-between">
              <div className="gt-label">Tickets ({data.tickets.length})</div>
              <button
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-[var(--gt-accent)] text-white font-bold border-2 border-[var(--gt-ink)]"
                onClick={() => onDeleteBooking(order.id)}
                data-testid="sa-drawer-delete-booking">
                <Trash2 size={12} /> Delete whole booking
              </button>
            </div>

            {/* Tickets */}
            <div className="space-y-2">
              {data.tickets.map((t) => {
                const dead = ["voided", "cancelled", "refunded"].includes(t.status);
                return (
                  <div key={t.id} className="border border-[var(--gt-line)] p-3" data-testid={`sa-ticket-row-${t.code}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{t.attendee_name || "(No name)"}</div>
                        <div className="text-xs text-[var(--gt-ink-2)] truncate">{t.attendee_email || ""}</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gt-ink-3)] mt-0.5">
                          {t.ticket_type_name} · {t.code}
                        </div>
                        {t.internal_test && <span className="text-[10px] text-[var(--gt-accent)] font-bold">INTERNAL/TEST</span>}
                        {!t.internal_test && t.visible_to_organiser === false && (
                          <span className="text-[10px] text-amber-700 font-bold ml-2" data-testid={`sa-hidden-badge-${t.code}`}>HIDDEN FROM ORGANISER</span>
                        )}
                        {t.voided_after_checkin && <span className="text-[10px] text-amber-700 font-bold ml-2">voided after check-in</span>}
                      </div>
                      <StatusPill status={t.status} />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {!dead && t.status !== "checked_in" && (
                        <ActBtn testid={`sa-checkin-${t.code}`} disabled={busyId === t.id}
                          onClick={() => doAction(t.id, () => superAdminApi.checkin(t.id, { reason: "admin check-in" }), "Checked in")}>
                          <CheckCircle2 size={12} /> Check in
                        </ActBtn>
                      )}
                      {t.status === "checked_in" && (
                        <ActBtn testid={`sa-undo-${t.code}`} disabled={busyId === t.id}
                          onClick={() => doAction(t.id, () => superAdminApi.undoCheckin(t.id, { reason: "admin undo" }), "Check-in undone")}>
                          <Undo2 size={12} /> Undo check-in
                        </ActBtn>
                      )}
                      {!dead && (
                        <ActBtn testid={`sa-edit-${t.code}`} onClick={() => setEditing(t)}>
                          <Pencil size={12} /> Edit
                        </ActBtn>
                      )}
                      {!dead && !t.internal_test && (
                        <ActBtn testid={`sa-resend-${t.code}`} disabled={busyId === t.id}
                          onClick={() => doAction(t.id, () => adminApi.resendSingleTicket(t.id, {}), "Ticket resent")}>
                          <Mail size={12} /> Resend
                        </ActBtn>
                      )}
                      <ActBtn testid={`sa-download-${t.code}`} disabled={busyId === t.id}
                        onClick={() => doDownload(t)}>
                        <Download size={12} /> Download
                      </ActBtn>
                      {!dead && (
                        <button
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-[var(--gt-accent)] text-white font-bold border border-[var(--gt-ink)]"
                          onClick={() => onDeleteTicket(t.id)} data-testid={`sa-delete-ticket-${t.code}`}>
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                      {dead && t.internal_test && (
                        <ActBtn testid={`sa-purge-${t.code}`} disabled={busyId === t.id}
                          onClick={() => doPurge(t.id)}>
                          <ShieldAlert size={12} /> Purge
                        </ActBtn>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <HistorySection title="Refunds" testid="sa-history-refunds"
              rows={[
                ...(data.refunds || []),
                ...(data.manual_refunds || []),
              ]}
              render={(r) => {
                const isManual = (r.refund_source || "").startsWith("manual") || r.refund_method === "manual";
                return (
                  <div className="flex justify-between gap-2">
                    <span>{centsToAUD(r.amount_cents)} · {isManual ? "Manual" : "Stripe"}</span>
                    <span className="font-bold">{r.refund_status || r.status || "recorded"}</span>
                  </div>
                );
              }} />
            <HistorySection title="Scan history" testid="sa-history-scans" rows={data.scan_logs} render={(s) => (
              <div className="flex justify-between gap-2">
                <span className="font-mono">{s.ticket_code}</span>
                <span>{s.result} · {formatAuDate(s.scanned_at)}{s.was_offline ? " · offline" : ""}</span>
              </div>
            )} />
            <HistorySection title="Emails" testid="sa-history-emails" rows={data.emails} render={(e) => (
              <div className="flex justify-between gap-2">
                <span className="truncate">{e.kind}</span>
                <span>{e.status} · {formatAuDate(e.created_at)}</span>
              </div>
            )} />
            <HistorySection title="Audit timeline" testid="sa-history-audit" rows={data.audit} render={(a) => (
              <div>
                <div className="flex justify-between gap-2">
                  <span className="font-semibold">{a.action}</span>
                  <span className="text-[var(--gt-ink-3)]">{formatAuDate(a.created_at)}</span>
                </div>
                {(a.reason || a.actor_email) && (
                  <div className="text-[11px] text-[var(--gt-ink-3)]">{a.actor_email}{a.reason ? ` · ${a.reason}` : ""}</div>
                )}
              </div>
            )} />
          </div>
        )}
      </div>

      {editing && (
        <EditTicketDialog ticket={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onMutated && onMutated(); load(); }} />
      )}
    </div>
  );
}

function EditTicketDialog({ ticket, onClose, onSaved }) {
  const [name, setName] = useState(ticket.attendee_name || "");
  const [email, setEmail] = useState(ticket.attendee_email || "");
  const [phone, setPhone] = useState(ticket.attendee_phone || "");
  const [visible, setVisible] = useState(ticket.visible_to_organiser !== false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await superAdminApi.editTicket(ticket.id, {
        attendee_name: name, attendee_email: email || null, attendee_phone: phone || null,
        visible_to_organiser: visible, reason: reason || null,
      });
      toast.success("Attendee updated");
      onSaved();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="sa-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading font-black">Edit attendee</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input className="gt-input w-full" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="sa-edit-name" />
          <input className="gt-input w-full" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
          <input className="gt-input w-full" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
          {!ticket.internal_test && (
            <label className="flex items-start gap-2 text-sm border border-[var(--gt-line)] p-3" data-testid="sa-edit-visible-wrap">
              <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} data-testid="sa-edit-visible" />
              <span>Visible to organiser
                <span className="block text-[11px] text-[var(--gt-ink-3)]">
                  Off = hidden from the organiser's lists, search, exports & attendee reports. Scanner, capacity, payments & platform reconciliation are unaffected.
                </span>
              </span>
            </label>
          )}
          <input className="gt-input w-full" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" />
        </div>
        <DialogFooter>
          <button className="gt-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="gt-btn-primary" onClick={save} disabled={busy} data-testid="sa-edit-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActBtn({ children, onClick, disabled, testid }) {
  return (
    <button className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm border border-[var(--gt-line)] hover:bg-[var(--gt-line)]/40 disabled:opacity-40"
      onClick={onClick} disabled={disabled} data-testid={testid}>{children}</button>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[var(--gt-ink-3)]">{k}</span>
      <span className={`font-semibold text-right break-all ${mono ? "font-mono text-xs" : ""}`}>{v ?? "—"}</span>
    </div>
  );
}

function HistorySection({ title, rows, render, testid }) {
  return (
    <div data-testid={testid}>
      <div className="gt-label mb-2">{title} ({rows?.length || 0})</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-[var(--gt-ink-3)]">None.</div>
      ) : (
        <ul className="space-y-1 text-xs border border-[var(--gt-line)] p-3">
          {rows.map((r, i) => <li key={r.id || i} className="border-b border-[var(--gt-line)] last:border-0 pb-1 last:pb-0">{render(r)}</li>)}
        </ul>
      )}
    </div>
  );
}
